'use client';

import type { PostSearchProps } from '@routeroyal/theme-core';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

/**
 * Stone Investment Post Search
 * 
 * Minimal editorial-style search input with:
 * - Refined typography
 * - Subtle animations
 * - Elegant focus states
 */
export function StoneInvestmentPostSearch({
  value = '',
  onChange,
  placeholder = 'Search articles...',
  className = '',
}: PostSearchProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>(undefined);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      setLocalValue(value);
      lastEmittedRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleInputChange = (newValue: string) => {
    setLocalValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastEmittedRef.current = newValue;
      onChange(newValue);
    }, 300);
  };

  const handleClear = () => {
    setLocalValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastEmittedRef.current = '';
    onChange('');
  };

  return (
    <div className={`relative ${className}`}>
      <motion.div
        initial={false}
        animate={{
          borderColor: isFocused ? 'var(--color-text)' : 'var(--color-border)',
        }}
        transition={{ duration: 0.2 }}
        className="relative border-b"
      >
        {/* Search Icon */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Input */}
        <input
          type="text"
          value={localValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="
            w-full pl-7 pr-8 py-3
            bg-transparent
            text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]
            text-sm tracking-wide
            focus:outline-none
          "
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        />

        {/* Clear Button */}
        {localValue && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            type="button"
            onClick={handleClear}
            className="
              absolute right-0 top-1/2 -translate-y-1/2
              text-[var(--color-text-muted)] hover:text-[var(--color-text)]
              transition-colors
            "
            aria-label="Clear search"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </motion.button>
        )}
      </motion.div>
    </div>
  );
}
